import type { TFunction } from 'i18next';
import type { Contract, ContractReplacement } from '../types';
import { agreementLabel } from './agreementIdentity';

/**
 * Why a new ÄTA cannot be started (ClickUp 869fkk2jh). A contract has one
 * open ÄTA at a time; the API refuses a second one with 409. A draft that has
 * not been sent can still be continued, so it does not block.
 */
export type VariationBlockReason = 'signing' | 'deposit' | 'refund' | 'activation';

export type VariationBlock = { contract: Contract; reason: VariationBlockReason };

/** The version an ÄTA is created from: the current one in its chain. */
export function currentAgreementVersion(contract: Contract, contracts: Contract[]): Contract {
  let current = contract;
  if (current.settlementType === 'price_adjustment' && current.parentContractId) {
    current = contracts.find(c => c.id === current.parentContractId) ?? current;
  }
  const visited = new Set<string>();
  while (current.supersededByContractId && !visited.has(current.id)) {
    visited.add(current.id);
    const successorId = current.supersededByContractId;
    const next = contracts.find(c => c.id === successorId);
    if (!next) break;
    current = next;
  }
  return current;
}

const sentForSigning = (contract: Contract) =>
  contract.companySigned ||
  contract.personSigned ||
  !!contract.signingSentAt ||
  ['sent', 'pending_signature'].includes(contract.signingStatus ?? '');

export function variationBlock(
  contract: Contract,
  contracts: Contract[],
  replacements: Record<string, ContractReplacement> = {}
): VariationBlock | null {
  const original = currentAgreementVersion(contract, contracts);
  for (const candidate of contracts) {
    const changes = candidate.stageVariation?.originalContractId === original.id ||
      candidate.replacesContractId === original.id;
    if (!changes || candidate.id === original.id) continue;
    if (candidate.versionStatus === 'settlement_pending') {
      const status = replacements[candidate.id]?.status;
      return {
        contract: candidate,
        reason: status === 'awaiting_payment' ? 'deposit' : status === 'refund_pending' ? 'refund' : 'activation',
      };
    }
    if (candidate.versionStatus === 'proposed' && sentForSigning(candidate)) {
      return { contract: candidate, reason: 'signing' };
    }
  }
  return null;
}

export const variationBlockText = (block: VariationBlock, contracts: Contract[], t: TFunction) =>
  t(`variationWizard.blocked.${block.reason}`, { agreement: agreementLabel(block.contract, contracts, t) });

/** Thrown when an ÄTA is opened while another one on the contract is not finished. */
export class VariationBlockedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'VariationBlockedError';
  }
}

/** The text to show for a failed attempt to open an ÄTA. */
export const variationOpenErrorText = (error: unknown, fallback: string) =>
  error instanceof VariationBlockedError ? error.message : fallback;
